import React, { useState, useEffect } from "react";
import {
	Card,
	CardContent,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Paper,
	Grid,
	Box,
	Divider,
	TablePagination,
	Button,
	TextField,
	Chip,
	Stack,
	IconButton,
	Tooltip,
	useTheme,
	alpha,
	Skeleton,
	Alert,
} from "@mui/material";

import {
	MonetizationOn as RevenueIcon,
	TrendingUp as TrendingUpIcon,
	People as PeopleIcon,
	LocalActivity as ActivityIcon,
	Download as DownloadIcon,
	Refresh as RefreshIcon,
	CalendarMonth as CalendarIcon,
	ArrowUpward as ArrowUpIcon,
	ArrowDownward as ArrowDownIcon,
	Info as InfoIcon,
} from "@mui/icons-material";

import { gql, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { exportMetricsToExcel } from "../helpers/excell";


// Styled metric card with animation and improved visuals
function MetricCard({ title, value, icon, color, percentage, trend }) {
	const theme = useTheme();

	return (
		<Card
			elevation={0}
			sx={{
				display: "flex",
				borderRadius: 3,
				p: 3,
				height: "100%",
				background: `linear-gradient(135deg, ${alpha(color, 0.05)} 0%, ${alpha(color, 0.01)} 100%)`,
				border: `1px solid ${alpha(color, 0.1)}`,
				transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
				position: "relative",
				overflow: "hidden",
				"&:hover": {
					transform: "translateY(-4px)",
					boxShadow: `0 12px 20px -10px ${alpha(color, 0.3)}`,
					"& .metric-icon": {
						transform: "scale(1.1) translateY(-5px)",
						opacity: 0.9,
					},
				},
			}}
		>
			<Box sx={{ zIndex: 1, width: "100%" }}>
				<Stack direction="row" justifyContent="space-between" alignItems="flex-start">
					<Box>
						<Typography
							variant="body2"
							sx={{
								color: theme.palette.text.secondary,
								textTransform: "uppercase",
								fontWeight: 500,
								letterSpacing: 0.5,
								mb: 0.5,
							}}
						>
							{title}
						</Typography>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 700,
								color: theme.palette.text.primary,
								mb: 1,
							}}
						>
							{value}
						</Typography>

						{percentage && (
							<Stack direction="row" spacing={1} alignItems="center">
								<Chip
									size="small"
									icon={trend === "up" ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
									label={`${percentage}%`}
									sx={{
										backgroundColor: trend === "up" ? alpha("#4caf50", 0.1) : alpha("#f44336", 0.1),
										color: trend === "up" ? "#4caf50" : "#f44336",
										fontWeight: 600,
										height: 24,
									}}
								/>
								<Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
									vs. previous period
								</Typography>
							</Stack>
						)}
					</Box>

					<Box
						className="metric-icon"
						sx={{
							backgroundColor: alpha(color, 0.15),
							color: color,
							width: 48,
							height: 48,
							borderRadius: 2,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
						}}
					>
						{icon}
					</Box>
				</Stack>
			</Box>

			{/* Background decorative element */}
			<Box
				sx={{
					position: "absolute",
					right: -30,
					bottom: -30,
					width: 140,
					height: 140,
					borderRadius: "50%",
					backgroundColor: alpha(color, 0.04),
					zIndex: 0,
				}}
			/>
		</Card>
	);
}

// Modern date range selector
function DateRangeSelector({ startDate, endDate, setStartDate, setEndDate, onRefresh }) {
	const theme = useTheme();

	return (
		<Paper
			elevation={0}
			sx={{
				p: 2,
				borderRadius: 3,
				border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
				background: alpha(theme.palette.background.paper, 0.8),
				backdropFilter: "blur(8px)",
			}}
		>
			<Grid container spacing={2} alignItems="center">
				<Grid item xs={12} sm={5} md={4} lg={3}>
					<TextField
						label="Data Início"
						type="date"
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
						fullWidth
						variant="outlined"
						size="small"
						InputProps={{
							startAdornment: (
								<CalendarIcon sx={{ color: theme.palette.primary.main, mr: 1, opacity: 0.7 }} fontSize="small" />
							),
						}}
					/>
				</Grid>
				<Grid item xs={12} sm={5} md={4} lg={3}>
					<TextField
						label="Data Fim"
						type="date"
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
						fullWidth
						variant="outlined"
						size="small"
						InputProps={{
							startAdornment: (
								<CalendarIcon sx={{ color: theme.palette.primary.main, mr: 1, opacity: 0.7 }} fontSize="small" />
							),
						}}
					/>
				</Grid>
				<Grid item xs={12} sm={2} md={2} lg={2}>
					<Tooltip title="Atualizar dados">
						<Button
							variant="contained"
							color="primary"
							fullWidth
							onClick={onRefresh}
							startIcon={<RefreshIcon />}
							sx={{
								borderRadius: 2,
								boxShadow: theme.shadows[1],
								height: 40,
							}}
						>
							Atualizar
						</Button>
					</Tooltip>
				</Grid>
			</Grid>
		</Paper>
	);
}

const RENTING_METRICS_QUERY = gql`
  query GetRentingMetrics($startDate: String, $endDate: String) {
    rentingMetrics(startDate: $startDate, endDate: $endDate)
  }
`;

function Dashboard() {
	const theme = useTheme();
	const { t } = useTranslation();

	// Date initialization
	const currentDate = new Date();
	const currentYear = currentDate.getFullYear();
	const currentMonth = currentDate.getMonth();
	const startOfMonth = new Date(currentYear, currentMonth, 1);
	const endOfMonth = new Date(currentYear, currentMonth + 1, 0);
	const formatDate = (date) => date.toISOString().split("T")[0];

	// State
	const [startDate, setStartDate] = useState(formatDate(startOfMonth));
	const [endDate, setEndDate] = useState(formatDate(endOfMonth));
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [sortColumn, setSortColumn] = useState("timesUsed");
	const [sortDirection, setSortDirection] = useState("desc");

	// Query data
	const { loading, error, data, refetch } = useQuery(RENTING_METRICS_QUERY, {
		variables: { startDate, endDate },
	});

	// Refresh data when dates change
	useEffect(() => {
		refetch();
	}, [startDate, endDate, refetch]);

	// Handle manual refresh
	const handleRefresh = () => {
		refetch();
	};

	// Handle sorting
	const handleSort = (column) => {
		const isAsc = sortColumn === column && sortDirection === "asc";
		setSortDirection(isAsc ? "desc" : "asc");
		setSortColumn(column);
	};

	// Loading state
	if (loading) {
		return (
			<Box>
				<Grid container spacing={3}>
					<Grid item xs={12}>
						<Skeleton variant="rectangular" height={60} sx={{ borderRadius: 3, mb: 4 }} />
					</Grid>
					{[1, 2, 3, 4].map((item) => (
						<Grid item xs={12} sm={6} md={3} key={item}>
							<Skeleton variant="rectangular" height={140} sx={{ borderRadius: 3 }} />
						</Grid>
					))}
					<Grid item xs={12} mt={2}>
						<Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
					</Grid>
				</Grid>
			</Box>
		);
	}

	// Error state
	if (error) {
		return (
			<Box>
				<Alert
					severity="error"
					sx={{
						borderRadius: 3,
						"& .MuiAlert-icon": {
							fontSize: 28,
						},
					}}
				>
					<Typography variant="h6">{t("GENERIC.ERROR")}</Typography>
					<Typography variant="body2">{error.message}</Typography>
					<Button color="error" variant="outlined" sx={{ mt: 2 }} onClick={handleRefresh}>
						Tentar novamente
					</Button>
				</Alert>
			</Box>
		);
	}

	const metrics = data?.rentingMetrics || {};

	// Merge machine data
	const mergedMachinesData =
		metrics.machineUsageChances
			?.map((usage) => {
				const revenueDetail = metrics.machinesRevenueDetails.find(
					(detail) => detail.machine === usage.machine
				);
				return {
					...usage,
					...revenueDetail,
				};
			})
			.filter((machine, index, self) => index === self.findIndex((m) => m.machine === machine.machine)) ||
		[];

	// Sort data based on current sort settings
	const sortedMachineData = [...mergedMachinesData].sort((a, b) => {
		if (sortDirection === "asc") {
			return a[sortColumn] - b[sortColumn];
		} else {
			return b[sortColumn] - a[sortColumn];
		}
	});

	// Calcular a máquina com melhor desempenho (a que mais rendeu)
	const bestPerformingMachine = mergedMachinesData.reduce((best, current) => {
		return !best || current.totalRevenue > best.totalRevenue ? current : best;
	}, null);

	// Format metrics for display
	const totalRevenue = `${metrics.totalRevenue?.toLocaleString() || 0}€`;
	const avgDailyRevenue = `${metrics.averageDailyRevenue?.toFixed(2) || "0"}€`;
	const rentalCount = metrics.numberOfRentals || 0;
	const activeRentals = metrics.activeRentals || 0;

	return (
		<Box
			p={3}
			sx={{
				minHeight: "100vh",
			}}
		>
			{/* Page Header with Export Button */}
			<Stack
				direction={{ xs: "column", sm: "row" }}
				justifyContent="space-between"
				alignItems={{ xs: "flex-start", sm: "center" }}
				mb={4}
				spacing={2}
			>
				<Box>
					<Typography
						variant="h4"
						sx={{
							fontWeight: 700,
							color: theme.palette.text.primary,
							mb: 1,
						}}
					>
						{t("DASHBOARD.RENTING_METRICS")}
					</Typography>
					<Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
						Estatísticas de aluguer para o período selecionado
					</Typography>
				</Box>

				<Button
					variant="contained"
					color="primary"
					startIcon={<DownloadIcon />}
					onClick={() => exportMetricsToExcel(metrics, mergedMachinesData, "DashboardMetrics")}
					sx={{
						borderRadius: 2,
						py: 1.2,
						px: 3,
						boxShadow: "0 8px 16px rgba(25, 118, 210, 0.15)",
						background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
						"&:hover": {
							boxShadow: "0 12px 20px rgba(25, 118, 210, 0.25)",
						},
						transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
					}}
				>
					{t("DASHBOARD.EXPORT_EXCEL")}
				</Button>
			</Stack>

			{/* TOConline Integration */}


			{/* Date Selector */}
			<Box mb={4}>
				<DateRangeSelector
					startDate={startDate}
					endDate={endDate}
					setStartDate={setStartDate}
					setEndDate={setEndDate}
					onRefresh={handleRefresh}
				/>
			</Box>

			{/* Metrics Cards */}
			<Grid container spacing={3} mb={4}>
				<Grid item xs={12} sm={6} md={3}>
					<MetricCard title={t("DASHBOARD.TOTAL_REVENUE")} value={totalRevenue} icon={<RevenueIcon />} color="#2196f3" />
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<MetricCard title={t("DASHBOARD.AVERAGE_DAILY_REVENUE")} value={avgDailyRevenue} icon={<TrendingUpIcon />} color="#ff9800" />
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<MetricCard title={t("DASHBOARD.NUMBER_OF_RENTALS")} value={rentalCount} icon={<PeopleIcon />} color="#4caf50" />
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<MetricCard title={t("DASHBOARD.ACTIVE_RENTALS")} value={activeRentals} icon={<ActivityIcon />} color="#f44336" />
				</Grid>
			</Grid>

			{/* Machine Usage Table */}
			<Grid container spacing={3} mt={5}>
				<Grid item xs={12}>
					<Paper
						elevation={0}
						sx={{
							borderRadius: 3,
							overflow: "hidden",
							border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
							boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
						}}
					>
						<Box
							sx={{
								p: 3,
								borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
								backgroundColor: alpha(theme.palette.background.paper, 0.5),
							}}
						>
							<Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
								{t("DASHBOARD.MACHINE_USAGE")}
							</Typography>
							<Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
								Desempenho de cada máquina durante o período selecionado
							</Typography>
						</Box>

						<Box sx={{ overflowX: "auto" }}>
							<Table>
								<TableHead>
									<TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
										<TableCell>
											<Typography variant="subtitle2" fontWeight={600}>
												{t("GENERIC.MACHINES")}
											</Typography>
										</TableCell>

										{/* Sortable column header */}
										<TableCell align="right">
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													justifyContent: "flex-end",
													cursor: "pointer",
												}}
												onClick={() => handleSort("timesUsed")}
											>
												<Typography variant="subtitle2" fontWeight={600}>
													{t("DASHBOARD.TIMES_USED")}
												</Typography>
												<Box sx={{ display: "flex", flexDirection: "column", ml: 0.5 }}>
													<ArrowUpIcon
														fontSize="small"
														sx={{
															fontSize: 14,
															color:
																sortColumn === "timesUsed" && sortDirection === "asc"
																	? theme.palette.primary.main
																	: alpha(theme.palette.text.primary, 0.3),
														}}
													/>
													<ArrowDownIcon
														fontSize="small"
														sx={{
															fontSize: 14,
															mt: -1,
															color:
																sortColumn === "timesUsed" && sortDirection === "desc"
																	? theme.palette.primary.main
																	: alpha(theme.palette.text.primary, 0.3),
														}}
													/>
												</Box>
											</Box>
										</TableCell>

										<TableCell align="right">
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													justifyContent: "flex-end",
													cursor: "pointer",
												}}
												onClick={() => handleSort("usageChances")}
											>
												<Typography variant="subtitle2" fontWeight={600}>
													{t("DASHBOARD.USAGE_CHANCES")}
												</Typography>
												<Box sx={{ display: "flex", flexDirection: "column", ml: 0.5 }}>
													<ArrowUpIcon
														fontSize="small"
														sx={{
															fontSize: 14,
															color:
																sortColumn === "usageChances" && sortDirection === "asc"
																	? theme.palette.primary.main
																	: alpha(theme.palette.text.primary, 0.3),
														}}
													/>
													<ArrowDownIcon
														fontSize="small"
														sx={{
															fontSize: 14,
															mt: -1,
															color:
																sortColumn === "usageChances" && sortDirection === "desc"
																	? theme.palette.primary.main
																	: alpha(theme.palette.text.primary, 0.3),
														}}
													/>
												</Box>
											</Box>
										</TableCell>

										<TableCell align="right">
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													justifyContent: "flex-end",
													cursor: "pointer",
												}}
												onClick={() => handleSort("revenueSinceStarted")}
											>
												<Typography variant="subtitle2" fontWeight={600}>
													{t("DASHBOARD.REVENUE_SINCE_STARTED")}
												</Typography>
												<Box sx={{ display: "flex", flexDirection: "column", ml: 0.5 }}>
													<ArrowUpIcon
														fontSize="small"
														sx={{
															fontSize: 14,
															color:
																sortColumn === "revenueSinceStarted" && sortDirection === "asc"
																	? theme.palette.primary.main
																	: alpha(theme.palette.text.primary, 0.3),
														}}
													/>
													<ArrowDownIcon
														fontSize="small"
														sx={{
															fontSize: 14,
															mt: -1,
															color:
																sortColumn === "revenueSinceStarted" && sortDirection === "desc"
																	? theme.palette.primary.main
																	: alpha(theme.palette.text.primary, 0.3),
														}}
													/>
												</Box>
											</Box>
										</TableCell>

										<TableCell align="right">
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													justifyContent: "flex-end",
													cursor: "pointer",
												}}
												onClick={() => handleSort("totalRevenue")}
											>
												<Typography variant="subtitle2" fontWeight={600}>
													{t("DASHBOARD.TOTAL_REVENUE")}
												</Typography>
												<Box sx={{ display: "flex", flexDirection: "column", ml: 0.5 }}>
													<ArrowUpIcon
														fontSize="small"
														sx={{
															fontSize: 14,
															color:
																sortColumn === "totalRevenue" && sortDirection === "asc"
																	? theme.palette.primary.main
																	: alpha(theme.palette.text.primary, 0.3),
														}}
													/>
													<ArrowDownIcon
														fontSize="small"
														sx={{
															fontSize: 14,
															mt: -1,
															color:
																sortColumn === "totalRevenue" && sortDirection === "desc"
																	? theme.palette.primary.main
																	: alpha(theme.palette.text.primary, 0.3),
														}}
													/>
												</Box>
											</Box>
										</TableCell>
									</TableRow>
								</TableHead>

								<TableBody>
									{sortedMachineData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((machine, index) => (
										<TableRow
											key={machine.machine}
											sx={{
												transition: "background-color 0.2s",
												"&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
												"&:nth-of-type(odd)": { backgroundColor: alpha(theme.palette.background.default, 0.5) },
											}}
										>
											<TableCell>
												<Stack direction="row" alignItems="center" spacing={1}>
													<Box
														sx={{
															width: 32,
															height: 32,
															borderRadius: "50%",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															backgroundColor: `${["#2196f3", "#4caf50", "#ff9800", "#f44336", "#9c27b0"][index % 5]}15`,
															color: ["#2196f3", "#4caf50", "#ff9800", "#f44336", "#9c27b0"][index % 5],
															fontWeight: 600,
															fontSize: 14,
														}}
													>
														{machine.machine.charAt(0).toUpperCase()}
													</Box>
													<Typography fontWeight={500}>{machine.machine}</Typography>
												</Stack>
											</TableCell>
											<TableCell align="right">
												<Chip
													label={machine.timesUsed}
													size="small"
													sx={{
														fontWeight: 500,
														backgroundColor: alpha(theme.palette.primary.main, 0.1),
														color: theme.palette.primary.main,
													}}
												/>
											</TableCell>
											<TableCell align="right">{machine.usageChances}</TableCell>
											<TableCell align="right">
												<Typography fontWeight={600} color={theme.palette.success.main}>
													{machine.revenueSinceStarted.toLocaleString()}€
												</Typography>
											</TableCell>
											<TableCell align="right">
												<Typography fontWeight={600} color={theme.palette.success.dark}>
													{machine.totalRevenue.toLocaleString()}€
												</Typography>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</Box>

						<TablePagination
							component="div"
							count={mergedMachinesData.length}
							page={page}
							onPageChange={(event, newPage) => setPage(newPage)}
							rowsPerPage={rowsPerPage}
							labelRowsPerPage="Linhas por pagina"
							onRowsPerPageChange={(event) => setRowsPerPage(parseInt(event.target.value, 10))}
							rowsPerPageOptions={[5, 10, 25, 50]}
							sx={{
								borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
							}}
						/>
					</Paper>
				</Grid>

				{/* Additional Metrics */}
				<Grid item xs={12} md={6} mt={4}>
					<Paper
						elevation={0}
						sx={{
							borderRadius: 3,
							overflow: "hidden",
							border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
							boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
							height: "100%",
						}}
					>
						<Box
							sx={{
								p: 3,
								borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
								backgroundColor: alpha(theme.palette.background.paper, 0.5),
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
								{t("DASHBOARD.ADDITIONAL_METRICS")}
							</Typography>
							<Tooltip title="Métricas adicionais sobre os alugueres">
								<IconButton size="small">
									<InfoIcon fontSize="small" color="primary" />
								</IconButton>
							</Tooltip>
						</Box>

						<Box sx={{ p: 3 }}>
							{/* Average Rental Duration */}
							<Card
								elevation={0}
								sx={{
									p: 2,
									mb: 2,
									borderRadius: 2,
									border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
									backgroundColor: alpha(theme.palette.primary.main, 0.02),
								}}
							>
								<Stack direction="row" justifyContent="space-between" alignItems="center">
									<Box>
										<Typography variant="subtitle2" color="text.secondary">
											{t("DASHBOARD.AVERAGE_RENTAL_DURATION")}
										</Typography>
										<Typography variant="h5" fontWeight={600} color="text.primary">
											{metrics.averageRentalDuration?.toFixed(2) || "0"}
											<Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
												{t("GENERIC.DAYS")}
											</Typography>
										</Typography>
									</Box>
									<Box
										sx={{
											width: 40,
											height: 40,
											borderRadius: "50%",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											backgroundColor: alpha(theme.palette.primary.main, 0.1),
											color: theme.palette.primary.main,
										}}
									>
										<CalendarIcon />
									</Box>
								</Stack>
							</Card>

							{/* Longest Rental */}
							<Card
								elevation={0}
								sx={{
									p: 2,
									mb: 2,
									borderRadius: 2,
									border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
									backgroundColor: alpha(theme.palette.success.main, 0.02),
								}}
							>
								<Stack direction="row" justifyContent="space-between" alignItems="center">
									<Box>
										<Typography variant="subtitle2" color="text.secondary">
											{t("DASHBOARD.LONGEST_RENTAL")}
										</Typography>
										<Typography variant="h5" fontWeight={600} color="success.dark">
											{metrics.longestRental || "0"}
											<Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
												{t("GENERIC.DAYS")}
											</Typography>
										</Typography>
									</Box>
									<Box
										sx={{
											width: 40,
											height: 40,
											borderRadius: "50%",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											backgroundColor: alpha(theme.palette.success.main, 0.1),
											color: theme.palette.success.main,
										}}
									>
										<TrendingUpIcon />
									</Box>
								</Stack>
							</Card>

							{/* Shortest Rental */}
							<Card
								elevation={0}
								sx={{
									p: 2,
									mb: 2,
									borderRadius: 2,
									border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
									backgroundColor: alpha(theme.palette.warning.main, 0.02),
								}}
							>
								<Stack direction="row" justifyContent="space-between" alignItems="center">
									<Box>
										<Typography variant="subtitle2" color="text.secondary">
											{t("DASHBOARD.SHORTEST_RENTAL")}
										</Typography>
										<Typography variant="h5" fontWeight={600} color="warning.dark">
											{metrics.shortestRental || "0"}
											<Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
												{t("GENERIC.DAYS")}
											</Typography>
										</Typography>
									</Box>
									<Box
										sx={{
											width: 40,
											height: 40,
											borderRadius: "50%",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											backgroundColor: alpha(theme.palette.warning.main, 0.1),
											color: theme.palette.warning.main,
										}}
									>
										<ArrowDownIcon />
									</Box>
								</Stack>
							</Card>

							{/* Additional calculated metric - Occupancy Rate */}
							<Card
								elevation={0}
								sx={{
									p: 2,
									borderRadius: 2,
									border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
									backgroundColor: alpha(theme.palette.info.main, 0.02),
								}}
							>
								<Stack direction="row" justifyContent="space-between" alignItems="center">
									<Box>
										<Typography variant="subtitle2" color="text.secondary">
											Taxa de Ocupação
										</Typography>
										<Typography variant="h5" fontWeight={600} color="info.dark">
											{(metrics.activeRentals / Math.max(mergedMachinesData.length, 1) * 100).toFixed(1)}%
										</Typography>
									</Box>
									<Box
										sx={{
											width: 40,
											height: 40,
											borderRadius: "50%",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											backgroundColor: alpha(theme.palette.info.main, 0.1),
											color: theme.palette.info.main,
										}}
									>
										<PeopleIcon />
									</Box>
								</Stack>
							</Card>
						</Box>
					</Paper>
				</Grid>

				{/* Performance Summary Card */}
				<Grid item xs={12} md={6} mt={4}>
					<Paper
						elevation={0}
						sx={{
							borderRadius: 3,
							overflow: "hidden",
							border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
							boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
							height: "100%",
						}}
					>
						<Box
							sx={{
								p: 3,
								borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
								backgroundColor: alpha(theme.palette.background.paper, 0.5),
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
								Resumo de Desempenho
							</Typography>
							<Chip label="Este período" size="small" color="primary" variant="outlined" />
						</Box>

						<Box sx={{ p: 3 }}>
							{/* Top Performing Machine */}
							{bestPerformingMachine && (
								<Box mb={3}>
									<Typography variant="subtitle2" color="text.secondary" gutterBottom>
										Máquina com Melhor Desempenho
									</Typography>
									<Card
										elevation={0}
										sx={{
											mt: 1,
											p: 2,
											borderRadius: 2,
											border: "1px solid",
											borderColor: alpha(theme.palette.success.main, 0.2),
											backgroundColor: alpha(theme.palette.success.main, 0.05),
										}}
									>
										<Stack direction="row" alignItems="center" spacing={2}>
											<Box
												sx={{
													width: 48,
													height: 48,
													borderRadius: "12px",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													backgroundColor: alpha(theme.palette.success.main, 0.15),
													color: theme.palette.success.main,
													fontSize: 20,
													fontWeight: 700,
												}}
											>
												{bestPerformingMachine.machine.charAt(0).toUpperCase()}
											</Box>
											<Box>
												<Typography variant="h6" fontWeight={600} color="text.primary">
													{bestPerformingMachine.machine || "N/A"}
												</Typography>
												<Stack direction="row" spacing={2} mt={0.5}>
													<Typography variant="caption" color="text.secondary">
														Uso: <b>{bestPerformingMachine.timesUsed || 0}x</b>
													</Typography>
													<Typography variant="caption" color="success.main" fontWeight={600}>
														{bestPerformingMachine.totalRevenue?.toLocaleString() || 0}€
													</Typography>
												</Stack>
											</Box>
										</Stack>
									</Card>
								</Box>
							)}

							{/* Revenue Distribution */}
							<Typography variant="subtitle2" color="text.secondary" gutterBottom>
								Distribuição de Receita por Máquina
							</Typography>
							<Box mt={2}>
								{sortedMachineData.slice(0, 5).map((machine, index) => {
									// Calculate percentage of total revenue
									const percentage = metrics.totalRevenue
										? ((machine.totalRevenue / metrics.totalRevenue) * 100).toFixed(1)
										: 0;
									return (
										<Box key={machine.machine} mb={2}>
											<Stack direction="row" justifyContent="space-between" mb={0.5}>
												<Typography variant="body2" fontWeight={500}>
													{machine.machine}
												</Typography>
												<Typography variant="body2" fontWeight={600}>
													{percentage}%
												</Typography>
											</Stack>
											<Box
												sx={{
													width: "100%",
													height: 8,
													backgroundColor: alpha(theme.palette.primary.main, 0.1),
													borderRadius: 4,
													overflow: "hidden",
												}}
											>
												<Box
													sx={{
														width: `${percentage}%`,
														height: "100%",
														backgroundColor: ["#2196f3", "#4caf50", "#ff9800", "#f44336", "#9c27b0"][index % 5],
														borderRadius: 4,
													}}
												/>
											</Box>
										</Box>
									);
								})}
							</Box>

							{/* Quick Stats */}
							<Stack
								direction="row"
								spacing={2}
								mt={3}
								sx={{
									p: 2,
									borderRadius: 2,
									backgroundColor: alpha(theme.palette.background.default, 0.7),
									border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
								}}
							>
								<Box sx={{ flex: 1 }}>
									<Typography variant="caption" color="text.secondary">
										Média de Receita/Máquina
									</Typography>
									<Typography variant="h6" fontWeight={600} color="primary.main">
										{mergedMachinesData.length > 0
											? (metrics.totalRevenue / mergedMachinesData.length).toFixed(2)
											: 0}
										€
									</Typography>
								</Box>
								<Divider orientation="vertical" flexItem />
								<Box sx={{ flex: 1 }}>
									<Typography variant="caption" color="text.secondary">
										Taxa de Aluguer/Dia
									</Typography>
									<Typography variant="h6" fontWeight={600} color="primary.main">
										{(() => {
											// Calculate date difference in days
											const start = new Date(startDate);
											const end = new Date(endDate);
											const diffTime = Math.abs(end - start);
											const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
											return (metrics.numberOfRentals / Math.max(diffDays, 1)).toFixed(1);
										})()}
									</Typography>
								</Box>
							</Stack>
						</Box>
					</Paper>
				</Grid>
			</Grid>

			{/* Footer Spacing */}
			<Box mt={6} />
		</Box>
	);
}

export default Dashboard;
